import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
  FC,
} from "react";
import debounce from "debounce";
import { useLocation } from "react-router";
import { scrollTo } from "src/utils/scroll";

// this component observes scroll position and updates the URL hash
// based on which section is currently in the viewport.

const DELAY = 150; //ms

const ActiveSectionContext = createContext<string>(null);
export const useActiveSection = () => useContext(ActiveSectionContext);

interface Props {
  children: ReactNode;
  sections: string[];
}

export const HashObserver: FC<Props> = ({ children, sections }) => {
  const [activeSection, setActiveSection] = useState<string>(null);

  const location = useLocation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setUrlHash = useCallback(
    debounce((hash: string) => {
      const adjustedHash = hash.startsWith("#") ? hash : `#${hash}`;
      const newHash = hash ? adjustedHash : " "; // only empty space overrides prev hash
      window.history.replaceState(undefined, "", newHash);
      setActiveSection(hash);
    }, DELAY),
    []
  );

  useEffect(() => {
    if (location.hash) setTimeout(() => scrollTo(location.hash), DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const appElement = document.querySelector(".App");
    const sectionsElements = sections.map((id) => document.getElementById(id));

    const handleScroll = debounce(() => {
      const appRect = appElement.getBoundingClientRect();
      const appHeight = appRect.height;

      let sectionInView = false;

      sectionsElements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        const elementTop = elementRect.top - appRect.top;
        const elementBottom = elementRect.bottom - appRect.top;

        // check if the section is in the middle of the view
        const isInView =
          elementTop <= appHeight / 2 && elementBottom >= appHeight / 2;

        if (isInView) {
          setUrlHash(element.id);
          sectionInView = true;
        }
      });

      // if no section is in view, clear the hash
      if (!sectionInView) setUrlHash("");
    }, DELAY);

    appElement?.addEventListener("scroll", handleScroll);

    return () => {
      appElement?.removeEventListener("scroll", handleScroll);
    };
  }, [sections, setUrlHash]);

  return (
    <ActiveSectionContext.Provider value={activeSection}>
      {children}
    </ActiveSectionContext.Provider>
  );
};
